import { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { 
  Plus, 
  Edit, 
  Trash2, 
  FileText, 
  Eye, 
  EyeOff,
  Save,
  X,
  Info,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Heading1,
  Heading2,
  Heading3,
  Minus
} from 'lucide-react';

interface Template {
  id: string;
  nome: string;
  tipo: 'venda' | 'locacao' | 'prestacao_servico';
  conteudo_html: string | null;
  ativo: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export default function TemplatesContrato() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showVariables, setShowVariables] = useState(false);

  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'venda' as 'venda' | 'locacao' | 'prestacao_servico',
    conteudo_html: '',
    ativo: true
  });

  const variaveisDisponiveis = {
    processo: [
      { var: '{{data_atual}}', desc: 'Data atual' },
      { var: '{{titulo_processo}}', desc: 'Título do processo' },
      { var: '{{unidade}}', desc: 'Unidade/apartamento' }
    ],
    comprador: [
      { var: '{{comprador_nome}}', desc: 'Nome do comprador' },
      { var: '{{comprador_cpf_cnpj}}', desc: 'CPF/CNPJ do comprador' },
      { var: '{{comprador_email}}', desc: 'Email do comprador' },
      { var: '{{comprador_telefone}}', desc: 'Telefone do comprador' },
      { var: '{{comprador_endereco_completo}}', desc: 'Endereço completo do comprador' }
    ],
    vendedor: [
      { var: '{{vendedor_nome}}', desc: 'Nome do vendedor' },
      { var: '{{vendedor_cpf_cnpj}}', desc: 'CPF/CNPJ do vendedor' },
      { var: '{{vendedor_email}}', desc: 'Email do vendedor' },
      { var: '{{vendedor_telefone}}', desc: 'Telefone do vendedor' },
      { var: '{{vendedor_endereco_completo}}', desc: 'Endereço completo do vendedor' }
    ],
    locatario: [
      { var: '{{locatario_nome}}', desc: 'Nome do locatário' },
      { var: '{{locatario_cpf_cnpj}}', desc: 'CPF/CNPJ do locatário' },
      { var: '{{locatario_email}}', desc: 'Email do locatário' },
      { var: '{{locatario_telefone}}', desc: 'Telefone do locatário' },
      { var: '{{locatario_endereco_completo}}', desc: 'Endereço completo do locatário' }
    ],
    locador: [
      { var: '{{locador_nome}}', desc: 'Nome do locador' },
      { var: '{{locador_cpf_cnpj}}', desc: 'CPF/CNPJ do locador' },
      { var: '{{locador_email}}', desc: 'Email do locador' },
      { var: '{{locador_telefone}}', desc: 'Telefone do locador' },
      { var: '{{locador_endereco_completo}}', desc: 'Endereço completo do locador' }
    ],
    fiador: [
      { var: '{{fiador_nome}}', desc: 'Nome do fiador' },
      { var: '{{fiador_cpf_cnpj}}', desc: 'CPF/CNPJ do fiador' },
      { var: '{{fiador_email}}', desc: 'Email do fiador' },
      { var: '{{fiador_telefone}}', desc: 'Telefone do fiador' },
      { var: '{{fiador_endereco_completo}}', desc: 'Endereço completo do fiador' }
    ],
    imovel: [
      { var: '{{imovel_endereco}}', desc: 'Endereço do imóvel' },
      { var: '{{imovel_tipo}}', desc: 'Tipo do imóvel' },
      { var: '{{valor_venda}}', desc: 'Valor de venda' },
      { var: '{{valor_locacao}}', desc: 'Valor da locação' }
    ]
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('templates_contrato')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const mappedData = (data || []).map((t: any) => ({
        id: t.id,
        nome: t.nome,
        tipo: t.tipo,
        conteudo_html: t.conteudo_html || t.conteudo || '',
        ativo: t.ativo ?? true,
        created_at: t.created_at,
        updated_at: t.updated_at
      }));
      
      setTemplates(mappedData);
    } catch (error: any) {
      console.error('Erro ao carregar templates:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os templates',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setFormData({
      nome: '',
      tipo: 'venda',
      conteudo_html: '',
      ativo: true
    });
    setEditingTemplate(null);
    setIsEditing(true);
  };

  const handleEdit = (template: Template) => {
    setFormData({
      nome: template.nome,
      tipo: template.tipo as 'venda' | 'locacao' | 'prestacao_servico',
      conteudo_html: template.conteudo_html || '',
      ativo: template.ativo ?? true
    });
    setEditingTemplate(template);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!formData.nome || !formData.conteudo_html) {
      toast({
        title: 'Atenção',
        description: 'Preencha todos os campos obrigatórios',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);

      if (editingTemplate) {
        const { error } = await supabase
          .from('templates_contrato')
          .update({
          nome: formData.nome,
          tipo: formData.tipo,
          conteudo_html: formData.conteudo_html,
          ativo: formData.ativo
          })
          .eq('id', editingTemplate.id);

        if (error) throw error;

        toast({
          title: 'Sucesso',
          description: 'Template atualizado com sucesso'
        });
      } else {
        const { error } = await supabase
          .from('templates_contrato')
          .insert({
            nome: formData.nome,
            tipo: formData.tipo,
            conteudo_html: formData.conteudo_html,
            ativo: formData.ativo
          });

        if (error) throw error;

        toast({
          title: 'Sucesso',
          description: 'Template criado com sucesso'
        });
      }

      setIsEditing(false);
      loadTemplates();
    } catch (error: any) {
      console.error('Erro ao salvar template:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível salvar o template',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este template?')) return;

    try {
      const { error } = await supabase
        .from('templates_contrato')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Template excluído com sucesso'
      });

      loadTemplates();
    } catch (error: any) {
      console.error('Erro ao excluir template:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir o template',
        variant: 'destructive'
      });
    }
  };

  const toggleAtivo = async (template: Template) => {
    try {
      const { error } = await supabase
        .from('templates_contrato')
        .update({ ativo: !template.ativo })
        .eq('id', template.id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: `Template ${!template.ativo ? 'ativado' : 'desativado'} com sucesso`
      });

      loadTemplates();
    } catch (error: any) {
      console.error('Erro ao atualizar status:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o status',
        variant: 'destructive'
      });
    }
  };

  const insertVariable = (variable: string) => {
    if (editor) {
      // Inserir a variável no formato {{variavel}} na posição do cursor
      editor.chain().focus().insertContent(`{{${variable}}} `).run();
    } else {
      setFormData(prev => ({
        ...prev,
        conteudo_html: prev.conteudo_html + ' {{' + variable + '}} '
      }));
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
    ],
    content: formData.conteudo_html,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        conteudo_html: editor.getHTML()
      }));
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[400px] px-4 py-3',
      },
    },
  });

  useEffect(() => {
    if (editor && formData.conteudo_html !== editor.getHTML()) {
      editor.commands.setContent(formData.conteudo_html);
    }
  }, [isEditing, editingTemplate]);

  const MenuBar = () => {
    if (!editor) {
      return null;
    }

    return (
      <div className="border border-border rounded-t-md bg-muted/50 p-2 flex flex-wrap gap-1">
        <Button
          type="button"
          variant={editor.isActive('bold') ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Negrito"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('italic') ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Itálico"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('underline') ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Sublinhado"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('strike') ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="Tachado"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        <Button
          type="button"
          variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          title="Título 1"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Título 2"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="Título 3"
        >
          <Heading3 className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        <Button
          type="button"
          variant={editor.isActive('bulletList') ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Lista com marcadores"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive('orderedList') ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Lista numerada"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        <Button
          type="button"
          variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title="Alinhar à esquerda"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title="Centralizar"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title="Alinhar à direita"
        >
          <AlignRight className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant={editor.isActive({ textAlign: 'justify' }) ? 'default' : 'outline'}
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          title="Justificar"
        >
          <AlignJustify className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Linha horizontal"
        >
          <Minus className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  if (loading && !isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">
            {editingTemplate ? 'Editar Template' : 'Novo Template'}
          </h2>
          <Button variant="ghost" onClick={() => setIsEditing(false)}>
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
        </div>

        <div className="grid gap-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Nome do Template *
              </label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Ex: Contrato de Venda Padrão"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Tipo *
              </label>
              <select
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value as 'venda' | 'locacao' | 'prestacao_servico' })}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="venda">Venda</option>
                <option value="locacao">Locação</option>
                <option value="prestacao_servico">Prestação de Serviço</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-foreground">
                Conteúdo do Contrato *
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowVariables(!showVariables)}
              >
                <Info className="h-4 w-4 mr-2" />
                {showVariables ? 'Ocultar' : 'Mostrar'} Variáveis
              </Button>
            </div>

            {showVariables && (
              <div className="bg-muted border border-border rounded-lg p-4 mb-2 max-h-64 overflow-y-auto">
                <p className="text-sm font-medium mb-2">Variáveis disponíveis (clique para inserir):</p>
                {Object.entries(variaveisDisponiveis).map(([categoria, vars]) => (
                  <div key={categoria} className="mb-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{categoria}</p>
                    <div className="flex flex-wrap gap-1">
                      {vars.map((v) => (
                        <button
                          key={v.var}
                          type="button"
                          onClick={() => insertVariable(v.var)}
                          className="text-xs px-2 py-1 bg-primary/10 hover:bg-primary/20 text-primary rounded border border-primary/20"
                          title={v.desc}
                        >
                          {v.var}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="border border-border rounded-md overflow-hidden">
              <MenuBar />
              <EditorContent 
                editor={editor} 
                className="min-h-[400px] max-h-[600px] overflow-y-auto border-t border-border bg-background"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Use a barra de ferramentas para formatar o texto. Clique nas variáveis acima para inserir dados dinâmicos.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="ativo"
              checked={formData.ativo}
              onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
              className="rounded border-border"
            />
            <label htmlFor="ativo" className="text-sm text-foreground">
              Template ativo (disponível para uso)
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Salvando...' : 'Salvar Template'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Templates de Contrato</h2>
          <p className="text-muted-foreground">Gerencie templates para geração automática de contratos</p>
        </div>
        <Button onClick={handleNew}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-12 bg-muted/50 rounded-lg border border-border">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">Nenhum template cadastrado</p>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4 mr-2" />
            Criar Primeiro Template
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-foreground">{template.nome}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      template.tipo === 'venda' 
                        ? 'bg-blue-100 text-blue-800' 
                        : template.tipo === 'locacao'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {template.tipo === 'venda' ? 'Venda' : template.tipo === 'locacao' ? 'Locação' : 'Prestação de Serviço'}
                    </span>
                    {template.ativo ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        Ativo
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs flex items-center gap-1">
                        <EyeOff className="h-3 w-3" />
                        Inativo
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {template.conteudo_html?.substring(0, 200) || 'Sem conteúdo'}...
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleAtivo(template)}
                    title={template.ativo ? 'Desativar' : 'Ativar'}
                  >
                    {template.ativo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(template)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
